/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useNavigate } from 'js/lib/useRouter';

/* eslint-disable no-restricted-imports */
import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { Button, Grid, Typography2, VisuallyHidden, breakpoints } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/item';

export type Props = {
  isComplete: boolean;
  variant?: 'primary' | 'secondary';
  className?: string;
  disabled?: boolean;
  nextItem?: ItemMetadata;
  markComplete: () => void;
};

const styles = {
  completed: css`
    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
  completedText: css`
    display: flex;
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-400);
    }
  `,
  text: css`
    margin-right: var(--cds-spacing-300);
    margin-left: var(--cds-spacing-50);
  `,
  nextItem: css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-400);
    }
  `,
};

export const ItemCompleteButton = ({
  isComplete,
  variant = 'primary',
  className,
  disabled,
  nextItem,
  markComplete,
}: Props) => {
  const navigate = useNavigate();

  const goToNextItem = () => {
    if (nextItem) {
      navigate(nextItem.getLink());
    }
  };

  return (
    <div data-testid="item-complete-container" className={className}>
      {isComplete ? (
        <Grid container direction="row" data-testid="completed" css={styles.completed}>
          <Grid item alignItems="center" data-testid="completed-text" css={styles.completedText}>
            <CheckIcon size="large" color="success" aria-hidden={true} />
            <div role="alert" aria-live="polite">
              <Typography2 variant="subtitleMedium" color="success" css={styles.text} component="div">
                <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
                  {_t('Item is completed.')}
                </VisuallyHidden>
                <span aria-hidden={true}>{_t('Completed')}</span>
              </Typography2>
            </div>
          </Grid>
          {!!nextItem && (
            <Grid item>
              <Button
                data-testid="next-item"
                css={styles.nextItem}
                variant="primary"
                type="submit"
                onClick={goToNextItem}
              >
                {_t('Go to next item')}
              </Button>
            </Grid>
          )}
        </Grid>
      ) : (
        <div>
          <Button
            data-testid="mark-complete"
            variant={variant}
            type="submit"
            disabled={disabled}
            onClick={markComplete}
          >
            {_t('Mark as completed')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ItemCompleteButton;
